"use client";

import { useEffect, useMemo, useState } from "react";
import { getMediaUrl } from "@/app/lib/media";
import ScrollReveal from "../animations/ScrollReveal";
import LogoLoop, { type LogoItem } from "../animations/LogoLoop";

interface ClientLogo {
  name: string;
  logo?: any;
  /** Instagram handle (no @) — used to pull the account's real profile photo
   *  via /api/instagram-avatar when no uploaded logo file exists yet. */
  handle?: string | null;
}

interface CasesInfiniteScrollProps {
  title?: string | null;
  subtitle?: string | null;
  logos: ClientLogo[];
  locale: "en" | "ar";
  /** Embeds inline (no section chrome, one small caption line instead of the
   *  eyebrow/title/subtitle block) for placement directly inside another
   *  section — e.g. under the hero's CTA button — instead of as its own
   *  standalone page section. */
  compact?: boolean;
}

function getHandleKey(handle: string) {
  return handle.replace(/^@/, "").toLowerCase();
}

export default function CasesInfiniteScroll({
  title,
  subtitle,
  logos,
  locale,
  compact = false,
}: CasesInfiniteScrollProps) {
  const isAr = locale === "ar";
  const [avatars, setAvatars] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const handles = Array.from(
      new Set(
        (logos || [])
          .filter((logo) => !getMediaUrl(logo.logo))
          .map((logo) => logo.handle)
          .filter((handle): handle is string => Boolean(handle))
      )
    );
    if (handles.length === 0) return;

    const controller = new AbortController();

    Promise.all(
      handles.map(async (handle) => {
        const key = getHandleKey(handle);
        try {
          const response = await fetch(`/api/instagram-avatar?handle=${encodeURIComponent(key)}`, {
            signal: controller.signal,
          });
          const data = await response.json();
          return [key, data.avatarUrl || null] as const;
        } catch {
          return [key, null] as const;
        }
      })
    ).then((entries) => {
      if (!controller.signal.aborted) setAvatars(Object.fromEntries(entries));
    });

    return () => controller.abort();
  }, [logos]);

  // Logos shown at their natural size (object-contain, no crop) with nothing
  // behind them — LogoLoop's own `src` item rendering already does exactly
  // that, so entries here are plain {src,alt} where an image resolved, and a
  // styled-text node only for the (rare) client with neither an uploaded logo
  // nor a resolvable Instagram avatar.
  const loopLogos: LogoItem[] = useMemo(
    () =>
      (logos || []).map((logo) => {
        // Original upload only — never the "card" size: card variants are
        // generated 768x512 fit-cover (center crop), which chops any logo
        // that isn't 3:2. The original is resized fit-inside, so it always
        // shows the full logo.
        const imgUrl = getMediaUrl(logo.logo);
        const avatarUrl = logo.handle ? avatars[getHandleKey(logo.handle)] : null;
        const resolvedSrc = imgUrl || avatarUrl;

        if (resolvedSrc) {
          const media = typeof logo.logo === 'object' && logo.logo ? logo.logo as { width?: number; height?: number } : null;
          const width = media?.width || 320;
          const height = media?.height || 90;
          const canOptimize = resolvedSrc.startsWith('/') || /^https:\/\/[^/]+\.r2\.dev\//i.test(resolvedSrc);
          const optimizerUrl = (size: number) => `/_next/image?url=${encodeURIComponent(resolvedSrc)}&w=${size}&q=70`;

          return {
            src: canOptimize ? optimizerUrl(750) : resolvedSrc,
            ...(canOptimize
              ? {
                  srcSet: `${optimizerUrl(384)} 384w, ${optimizerUrl(640)} 640w, ${optimizerUrl(750)} 750w`,
                  sizes: '(max-width: 767px) 320px, 560px',
                }
              : {}),
            width,
            height,
            alt: logo.name,
            title: logo.name,
          };
        }
        return {
          node: (
            <span className="text-lg font-bold tracking-wide text-brand-text/55 transition-colors duration-300 hover:text-brand-cta">
              {logo.name}
            </span>
          ),
          ariaLabel: logo.name,
        };
      }),
    [logos, avatars]
  );

  if (!logos || logos.length === 0) return null;

  // Fallback localized values if not set in CMS
  const displayTitle =
    title ||
    (isAr
      ? "عملائنا وشركاؤنا في النجاح"
      : "Our Clients & Partners in Success");
  const displaySubtitle =
    subtitle ||
    (isAr
      ? "نحظى بثقة كبرى الشركات والعلامات التجارية الرائدة في المملكة العربية السعودية."
      : "Trusted by leading companies and emerging brands across Saudi Arabia.");

  if (compact) {
    // pb-28 on mobile (vs. pb-16 previously): the fixed chat launcher
    // occupies roughly the bottom 88px of this same corner, and the
    // marquee's edge-to-edge logos were passing behind it there.
    return (
      <div className="relative z-10 mt-8 w-full pb-28 sm:pb-16 md:mt-10 md:pb-20" dir={isAr ? "rtl" : "ltr"}>
        <ScrollReveal mode="fade-up" className="mx-auto max-w-xl px-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 md:text-sm">
            {displaySubtitle}
          </p>
        </ScrollReveal>

        <ScrollReveal mode="scale" delay={0.15} className="mt-6 w-full">
          <LogoLoop
            logos={loopLogos}
            speed={45}
            direction={isAr ? "right" : "left"}
            logoHeight={90}
            gap={60}
            pauseOnHover
            scaleOnHover
            fadeOut={false}
            ariaLabel={isAr ? "شعارات العملاء والشركاء" : "Client and partner logos"}
            className="py-4"
          />
        </ScrollReveal>
      </div>
    );
  }

  return (
    <section
      className="py-24 md:py-32 bg-brand-bg text-brand-text overflow-hidden relative border-y border-brand-text/10"
      dir={isAr ? "rtl" : "ltr"}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-brand-bg via-brand-text/5 to-brand-bg pointer-events-none z-0" />

      <div className="mx-auto max-w-7xl px-6 relative z-10">
        <ScrollReveal mode="fade-up" className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-brand-purple">
            {isAr ? "شراكات الأثر" : "IMPACT PARTNERSHIPS"}
          </span>
          <h2 className="mt-4 font-heading text-4xl md:text-5xl font-bold leading-tight tracking-tight">
            {displayTitle}
          </h2>
          <div className="mx-auto mt-6 h-1 w-16 rounded-full bg-brand-purple" />
          <p className="mt-6 text-lg leading-relaxed text-brand-text/72">
            {displaySubtitle}
          </p>
        </ScrollReveal>
      </div>

      {/* Full-bleed logo marquee — a direct child of the section (not the
          max-w-7xl wrapper above) so it spans the whole section width instead
          of being squeezed into half a two-column layout. */}
      <ScrollReveal mode="scale" delay={0.2} className="relative z-10 mt-16 w-full">
        <LogoLoop
          logos={loopLogos}
          speed={45}
          direction={isAr ? "right" : "left"}
          logoHeight={122}
          gap={112}
          pauseOnHover
          scaleOnHover
          ariaLabel={isAr ? "شعارات العملاء والشركاء" : "Client and partner logos"}
          className="py-8"
        />
      </ScrollReveal>
    </section>
  );
}
